import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { FLOW_TYPE_IDS, buildFlowTypeOptions } from './flowTypes';

const useApprovalFlowApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/approval-flows?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/approval-flows', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/approval-flows/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/approval-flows/${ids[0]}`);
      } else {
        await api.post('/v1/approval-flows/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const flowTypeOptions = useMemo(() => buildFlowTypeOptions(t), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('approvalFlow.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'flowType', label: t('approvalFlow.flowType'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: FLOW_TYPE_IDS,
          renderValue: (v) => { const found = flowTypeOptions.find((o) => o.id === v); return found ? found.name : v; },
        },
        customFilterListOptions: { render: (v) => { const found = flowTypeOptions.find((o) => o.id === v); return found ? found.name : v; } },
        customBodyRender: (v) => {
          const found = flowTypeOptions.find((o) => o.id === v);
          return found ? found.name : v;
        },
      },
    },
    { name: 'stepCount', label: t('approvalFlow.stepCount'), options: { filter: false, sort: false } },
  ], [t, flowTypeOptions]);

  return { columns, data, fetching, pagination, create, update, _delete, flowTypeOptions };
};

export default useApprovalFlowApi;
